import React, { useEffect } from 'react';
import { GraphQLClient, ClientContext, useQuery } from 'graphql-hooks';
import { withRouter } from 'react-router-dom';

import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Cell, Grid, GridContainer } from 'formlabs-web-components/Grid';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import {
  CarouselWrapper as Carousel,
  Picture
} from 'formlabs-web-components/Media';
import { Header } from 'formlabs-web-components/Type';
import { Breadcrumbs } from '../navigation';

import { ProductConfigurator } from './ProductDetailPlugin';

import { analytics } from '../../utils';
import clientOnly from '../../hoc/clientOnly';

import query from './productPageHeroQuery';

import './ProductPageHeroPlugin.module.css';

const cdnUrl = `${
  process.env.RAZZLE_MAGENTO_MEDIA_URL
}/catalog/product/600x600`;

const ProductHero = props => {
  const [t] = useTranslation();

  const {
    sku,
    language,
    currency,
    location: { pathname }
  } = props;

  const { loading, error, data } = useQuery(query, {
    variables: { sku, path: pathname }
  });

  const hide = loading || error;

  useEffect(
    () => {
      // only run if query is resolved
      const name = get(data, 'productPageHero.product.name', null);
      if (!hide && name) {
        analytics.track('Product Viewed', {
          sku,
          name,
          brand: 'Formlabs',
          quantity: 1
        });
      }
    },
    [sku, data]
  );

  if (hide || !data || !data.productPageHero) {
    return null;
  }

  const {
    product,
    product: {
      name,
      media,
      bundleOptions,
      attributes: { description, image }
    },
    baseUrl
  } = data.productPageHero;

  const navigationEntries = [{ name: t('Store'), url: baseUrl }];

  if (name) {
    navigationEntries.push({ name });
  }

  const pictures = media
    ? media.filter(item => item.mediaType === 'image')
    : [];

  return (
    <div styleName="page">
      <div styleName="menu">
        <Breadcrumbs entries={navigationEntries} />
      </div>
      <GridContainer>
        <Grid direction="x" gutters="margin">
          <Cell mediumSize="12" smallOrder="2" mediumOrder="1">
            <div styleName="images">
              {pictures.length > 1 ? (
                <Carousel controlsPosition="bottom">
                  {pictures.map(picture => (
                    <Picture
                      fullBleed
                      key={picture.id}
                      largeImage={`${cdnUrl}${picture.file}`}
                    />
                  ))}
                </Carousel>
              ) : image.length ? (
                <Picture
                  fullBleed
                  key={image.id}
                  largeImage={`${cdnUrl}${image}`}
                  alt={t('Product image for {{name}}', { name: name })}
                />
              ) : null}
            </div>
            {bundleOptions && (
              <div
                styleName="long-desc"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </Cell>
          <Cell mediumSize="12" smallOrder="1" mediumOrder="2">
            <Header
              level={1}
              displayAs={3}
              weight={700}
              styles={{ marginBottom: 0 }}
            >
              {name}
            </Header>
            <p styleName="sku">{sku}</p>
            {!bundleOptions && (
              <div
                styleName="long-desc"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            <ProductConfigurator
              {...{
                ...product,
                language,
                currency
              }}
            />
          </Cell>
        </Grid>
      </GridContainer>
    </div>
  );
};

const ProductPageHeroContent = compose(
  connect(state => {
    return {
      language: state.i18n.language,
      currency: state.i18n.currency
    };
  }),
  withRouter,
  DisplayComponent
)(ProductHero);

const public_api = process.env.RAZZLE_public_graphql_uri
  ? process.env.RAZZLE_public_graphql_uri
  : process.env.RAZZLE_content_api_uri_base;

const client = new GraphQLClient({
  url: `${public_api}/graphql`,
  fetchOptions: {
    credentials: 'include'
  }
});

const ProductPageHeroPlugin = props => {
  return (
    <ClientContext.Provider value={client}>
      <ProductPageHeroContent {...props} />
    </ClientContext.Provider>
  );
};

export default clientOnly(ProductPageHeroPlugin);
